import { useCallback, useState } from 'react';
import type { APIState, Result } from '@api';
import { Failure, Success, useFMSAPI, useErrorMessage } from '@api';
import { environmentAtom } from '@data/fms/environment/states';
import { projectAtom } from '@data/auth';
import type { AxiosResponse, AxiosError } from 'axios';
import { useNotification } from '@data/notification';
import { useTranslation } from 'react-i18next';
import { useAtomCallback } from 'jotai/utils';
import type {
  RemoteMonitoringSettingAPISchema,
  ScheduleTemplateAPISchema,
} from './types';
import { CUSTOM_DATA_KEY, CUSTOM_DATA_NAMESPACE } from './constants';

const useRequest = <T>() => {
  const fmsAPI = useFMSAPI();

  const request = useCallback(
    async (
      projectId: string,
      environmentId: string,
      keyName: string,
      body: T,
    ): Promise<Result<true, AxiosResponse>> => {
      const queryParams = new URLSearchParams();
      queryParams.append('namespace', CUSTOM_DATA_NAMESPACE);
      queryParams.append('key_name', keyName);
      try {
        await fmsAPI.put(
          `/${projectId}/environments/${environmentId}/custom-data/?${queryParams.toString()}`,
          body,
        );
        return new Success(true);
      } catch (error) {
        return new Failure((error as AxiosError).response as AxiosResponse);
      }
    },
    [fmsAPI],
  );

  return {
    request,
  };
};

/**
 * 遠隔監視設定更新API
 */
export const usePutRemoteMonitoringSettingAPI = () => {
  const [state, setState] = useState<APIState>('none');
  const { notifyError, notifySuccess } = useNotification();
  const getErrorMessage = useErrorMessage();
  const { t } = useTranslation();
  const { request } = useRequest<RemoteMonitoringSettingAPISchema>();

  /**
   * 遠隔監視設定更新
   */
  const putRemoteMonitoringSetting = useAtomCallback(
    useCallback(
      async (get, _, body: RemoteMonitoringSettingAPISchema) => {
        const project = get(projectAtom);
        const environment = get(environmentAtom);
        if (!project || !environment) {
          notifyError({
            message: t(`api.fms.put_remote_monitoring_settings`, {
              status: 'failed',
            }),
          });
          setState('hasError');
          return false;
        }
        setState('loading');

        const res = await request(
          project.id,
          environment.environment_id,
          CUSTOM_DATA_KEY.REMOTE_MONITORING_SETTINGS,
          body,
        );

        if (res.isFailure()) {
          notifyError({
            message: `${t(`api.fms.put_remote_monitoring_settings`, {
              status: 'failed',
            })}: ${getErrorMessage(res)}`,
          });
          setState('hasError');
          return false;
        }

        notifySuccess({
          message: t(`api.fms.put_remote_monitoring_settings`, {
            status: 'successfully',
          }),
        });
        setState('hasValue');
        return res.value;
      },
      [notifyError, notifySuccess, request, t, getErrorMessage],
    ),
  );

  return {
    state,
    putRemoteMonitoringSetting,
  };
};

/**
 * スケジュールテンプレート更新API
 */
export const usePutScheduleTemplatesAPI = () => {
  const [state, setState] = useState<APIState>('none');
  const { notifyError, notifySuccess } = useNotification();
  const getErrorMessage = useErrorMessage();
  const { t } = useTranslation();
  const { request } = useRequest();

  /**
   * スケジュールテンプレート更新
   */
  const putScheduleTemplates = useAtomCallback(
    useCallback(
      async (
        get,
        _,
        body: ScheduleTemplateAPISchema,
        type: 'create' | 'update' | 'delete',
      ) => {
        const project = get(projectAtom);
        const environment = get(environmentAtom);
        if (!project || !environment) {
          notifyError({
            message: t(`api.fms.put_schedule_templates.${type}`, {
              status: 'failed',
            }),
          });
          setState('hasError');
          return false;
        }
        setState('loading');

        const res = await request(
          project.id,
          environment.environment_id,
          CUSTOM_DATA_KEY.SCHEDULE_TEMPLATE,
          body,
        );

        if (res.isFailure()) {
          notifyError({
            message: `${t(`api.fms.put_schedule_templates.${type}`, {
              status: 'failed',
            })}: ${getErrorMessage(res)}`,
          });
          setState('hasError');
          return false;
        }

        notifySuccess({
          message: t(`api.fms.put_schedule_templates.${type}`, {
            status: 'successfully',
          }),
        });
        setState('hasValue');
        return res.value;
      },
      [notifyError, notifySuccess, request, t, getErrorMessage],
    ),
  );

  return {
    state,
    putScheduleTemplates,
  };
};
